<template>
  <div class="vendor-form">
    <form @submit.stop.prevent="form.handleSubmit()">
      <form.Field
        name="name"
        :validators="{
          onBlur: yup.string().required('Name is required')
        }"
      >
        <template #default="{ field }">
          <BaseInput
            v-model="(field.state.value as string)"
            label="Name"
            :name="field.name"
            :error="field.state.meta.errors && field.state.meta.errors[0] as string"
            @blur="field.handleBlur"
            @input="(e) => typeof e === 'string' ? field.handleChange(e) : field.handleChange(e.target.value)"
          />
        </template>
      </form.Field>

      <form.Field
        name="email"
        :validators="{
          onBlur: yup.string().required('Email is required').email('Email is invalid'),
        }"
      >
        <template #default="{ field }">
          <BaseInput
            v-model="(field.state.value as string)"
            :name="field.name"
            type="email"
            label="Email"
            :error="field.state.meta.errors && field.state.meta.errors[0] as string"
            @blur="field.handleBlur"
            @input="(e) => typeof e === 'string' ? field.handleChange(e) : field.handleChange(e.target.value)"
          />
        </template>
      </form.Field>

      <form.Field
        name="phone"
        :validators="{
          onChange: yup.string().optional().matches(/^[0-9() -]*$/, 'Phone number is invalid'),
        }"
      >
        <template #default="{ field }">
          <BaseInputPhone
            v-model="(field.state.value as string)"
            :name="field.name"
            label="Phone"
            :error="field.state.meta.errors && field.state.meta.errors[0] as string"
            @blur="field.handleBlur"
            @input="(e) => typeof e === 'string' ? field.handleChange(e) : field.handleChange(e.target.value)"
            @change="(e) => e !== field.state.value && field.handleChange(e)"
          />
        </template>
      </form.Field>

      <form.Field name="address">
        <template #default="{ field }">
          <AddressAutoComplete
            :name="field.name"
            :value="field.state.value as string"
            label="Address"
            @blur="field.handleBlur"
            @input="(e) => field.handleChange(e)"
          />
        </template>
      </form.Field>

      <h6 class="mt-4 font-semibold">Logo</h6>
      <BaseButton
        variant="secondary"
        text="Upload Logo"
        type="button"
        icon-class="i-mdi-paperclip"
        class="mt-2"
        :class="{ 'cursor-not-allowed opacity-50': vendorName === '' }"
        :disabled="vendorName === ''"
        @click="open = true"
      />

      <div
        v-if="vendor.logo"
        class="relative w-[90px] h-[90px] rounded-lg border border-slate-200 flex items-center justify-center mt-2 bg-slate-100"
      >
        <img
          :src="vendor.logo"
          class="object-cover rounded w-[90px] h-[90px] cursor-pointer"
        >
        <span
          class="absolute top-0 right-0 p-1 flex justify-center items-center rounded-full border-transparent bg-black/50 hover:bg-black/75 transition duration-200 ease-in-out cursor-pointer overflow-hidden select-none size-[1.3rem]"
          @click="deleteLogo"
        >
          <span class="i-mdi-close text-white"></span>
        </span>
      </div>

      <hr class="mt-6">

      <div class="flex justify-end gap-2 mt-3">
        <form.Subscribe>
          <template #default="{ canSubmit, isSubmitting }">
            <BaseButton
              variant="base"
              text="Cancel"
              :to="{ name: 'ListVendors' }"
              :disabled="isSubmitting"
              :class="{ 'cursor-not-allowed opacity-50': isSubmitting }"
            />

            <BaseButton
              variant="primary"
              text="Save"
              type="submit"
              :disabled="!canSubmit || isSubmitting"
              :class="{ 'cursor-not-allowed opacity-50': !canSubmit || isSubmitting }"
            />
          </template>
        </form.Subscribe>
      </div>
    </form>

    <Modal
      v-model="open"
      title="Upload Logo"
      width="w-full md:w-1/2"
      close-button-text="Close"
      :no-escape="true"
      :no-backdrop="true"
      :no-close-button="true"
      @close="setVendorLogo"
    >
      <template #body>
        <div class="mt-4">
          <AppFileUpload
            :storage-path="`${vendorName}/vendors/logo/`"
            @on-file-upload-completed="fileUploadCompleted"
          />
        </div>
      </template>
    </Modal>
  </div>
</template>

<script lang="ts" setup>
import { Vendor } from '@/models/vendor'
import BaseInput from '@/components/base/BaseInput.vue'
import BaseButton from '@/components/base/BaseButton.vue'
import AddressAutoComplete from '@/components/base/AddressAutoComplete.vue'
import AppFileUpload from '@/components/app/AppFileUpload.vue'
import { PropType, ref } from 'vue'
import { useForm } from '@tanstack/vue-form'
import { yupValidator } from '@tanstack/yup-form-adapter'
import * as yup from 'yup'
import StorageService from '@/services/StorageService'
import Modal from '@/components/app/AppModal.vue'
import { useNotification } from '@kyvg/vue3-notification'
import BaseInputPhone from '@/components/base/BaseInputPhone.vue'

const props = defineProps({
  model: {
    type: Object as PropType<Vendor>,
    default: () => new Vendor()
  },
  saveFunction: {
    type: Function,
    required: true
  }
})

const vendor = ref({ ...props.model })
const { notify } = useNotification()

const form = useForm({
  defaultValues: vendor.value,
  validatorAdapter: yupValidator(),
  onSubmit: async ({ value }) => {
    const vendor = Object.fromEntries(Object.entries(value).filter(([, v]) => v !== undefined))  as unknown as Vendor
    vendor.logo = tempLogo.value
    await props.saveFunction(vendor)
  }
})

const vendorName = form.useStore((state) => state.values.name)
const tempLogo = ref(vendor.value.logo)
const open = ref(false)
const storageService = new StorageService()

const fileUploadCompleted = (attachments) => {
  if (attachments.length > 0) {
    tempLogo.value = attachments[0]
  } else {
    tempLogo.value = vendor.value.logo
  }
}

const setVendorLogo = async () => {
  if (tempLogo.value) {
    if (vendor.value.logo) {
      if (vendor.value.logo !== tempLogo.value) {
        try {
          await storageService.deleteAttachmentWithStyledImages(vendor.value.logo)
          vendor.value.logo = tempLogo.value
        } catch (error) {
          console.error(error)
          notify({
            type: 'error',
            title: 'Error',
            text: 'An error occurred while deleting the logo'
          })
        }
      }
    } else {
      vendor.value.logo = tempLogo.value
    }
  }
}

const deleteLogo = async () => {
  try {
    await storageService.deleteAttachmentWithStyledImages(vendor.value.logo)
    vendor.value.logo = ''
    tempLogo.value = ''
  } catch (error) {
    console.error(error)
    notify({
      type: 'error',
      title: 'Error',
      text: 'An error occurred while deleting the logo'
    })
  }
}
</script>
